import {
    AccidentalDamage,
    ElectronicsFailure,
    LiquidDamage,
    ScreenDamage,
} from 'utils/constants';

/* eslint-disable no-plusplus */
export const getImageBasePath = (imagePath: string) => {
    const imageStaticPath = process.env.NEXT_ASSET_PREFIX
        ? process.env.NEXT_ASSET_PREFIX.split('/next_assets')[0]
        : '';

    const basePath = imageStaticPath
        ? `${imageStaticPath}/_next_static`
        : '/_next_static';
    if (basePath === '/_next_static') {
        return imagePath;
    }
    return `${basePath}${imagePath}`;
};

export const getTncLink = () => {
    try {
        const staticPath = process.env.NEXT_ASSET_PREFIX
            ? process.env.NEXT_ASSET_PREFIX.split('/next_assets')[0]
            : '';
        return `${staticPath}/_next_static/static/dealer_tnc.html`;
    } catch {
        return `/_next_static/static/dealer_tnc.html`;
    }
};

export const uuid = (length: number) => {
    let result = '';
    const characters =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(
            Math.floor(Math.random() * charactersLength)
        );
    }
    return result;
};

export const getCoverage = (plan: string) => {
    try {
        if (plan.includes('Screen Damage')) {
            return [ScreenDamage];
        }
        if (plan.includes('Comprehensive')) {
            return [LiquidDamage, AccidentalDamage];
        }
        if (plan.includes('Extended Warranty')) {
            return [ElectronicsFailure];
        }
        return [];
    } catch {
        return [];
    }
};

export const whatsCoverText = (plan: string) => {
    try {
        if (plan?.includes('screen damage')) {
            return 'Screen damage';
        }
        if (plan?.includes('comprehensive')) {
            return 'Screen damage with liquid damage';
        }
        if (plan?.includes('extended warranty') || plan?.includes('_ew')) {
            return 'Electronical & mechanical failures';
        }
        return '-';
    } catch {
        return '-';
    }
};

export const getPlanName = (plan: string) => {
    try {
        if (plan?.includes('SD')) {
            return 'Screen damage protection';
        }
        if (plan?.includes('ADLD')) {
            return 'Total damage protection';
        }
        if (plan?.includes('DEC')) {
            return "Extended Warranty Plan - Dec'22 - Nov'23 Sale";
        }
        if (plan?.includes('EW')) {
            return 'Extended Warranty Offer for Current Month';
        }
        return plan;
    } catch {
        return plan;
    }
};

export function isEmpty(value: any): boolean {
    let isEmpty = true;
    Object.values(value).forEach((val) => {
        if (val) isEmpty = false;
    });
    return isEmpty;
}

export function debounce(func, wait = 0, options?: any) {
    let lastArgs;
    let lastThis;
    let maxWait;
    let result;
    let timerId;
    let lastCallTime;
    let lastInvokeTime = 0;
    let leading = false;
    let maxing = false;
    let trailing = true;

    if (typeof func !== 'function') {
        throw new TypeError('Expected a function');
    }

    function invokeFunc(time) {
        const args = lastArgs;
        const thisArg = lastThis;

        lastThis = undefined;
        lastArgs = undefined;
        lastInvokeTime = time;
        result = func.apply(thisArg, args);
        return result;
    }

    function trailingEdge(time) {
        timerId = undefined;

        // Only invoke if we have `lastArgs` which means `func` has been
        // debounced at least once.
        if (trailing && lastArgs) {
            return invokeFunc(time);
        }
        lastThis = undefined;
        lastArgs = undefined;
        return result;
    }

    function shouldInvoke(time) {
        const timeSinceLastCall = time - lastCallTime;
        const timeSinceLastInvoke = time - lastInvokeTime;

        // Either this is the first call, activity has stopped and we're at the
        // trailing edge, the system time has gone backwards and we're treating
        // it as the trailing edge, or we've hit the `maxWait` limit.
        return (
            lastCallTime === undefined ||
            timeSinceLastCall >= wait ||
            timeSinceLastCall < 0 ||
            (maxing && timeSinceLastInvoke >= maxWait)
        );
    }

    function remainingWait(time) {
        const timeSinceLastCall = time - lastCallTime;
        const timeSinceLastInvoke = time - lastInvokeTime;
        const timeWaiting = wait - timeSinceLastCall;

        return maxing
            ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
            : timeWaiting;
    }

    function timerExpired() {
        const time = Date.now();
        if (shouldInvoke(time)) {
            return trailingEdge(time);
        }
        // Restart the timer.
        timerId = setTimeout(timerExpired, remainingWait(time));
        return null;
    }

    if (typeof options === 'object') {
        leading = !!options.leading;
        maxing = 'maxWait' in options;
        maxWait = maxing
            ? Math.max(Number(options.maxWait) || 0, wait)
            : maxWait;
        trailing = 'trailing' in options ? !!options.trailing : trailing;
    }

    function leadingEdge(time) {
        // Reset any `maxWait` timer.
        lastInvokeTime = time;
        // Start the timer for the trailing edge.
        timerId = setTimeout(timerExpired, wait);
        // Invoke the leading edge.
        return leading ? invokeFunc(time) : result;
    }

    function cancel() {
        if (timerId !== undefined) {
            clearTimeout(timerId);
        }
        lastInvokeTime = 0;
        lastThis = undefined;
        lastArgs = undefined;
        lastCallTime = undefined;
        timerId = undefined;
    }

    function flush() {
        return timerId === undefined ? result : trailingEdge(Date.now());
    }

    function debounced(...args) {
        const time = Date.now();
        const isInvoking = shouldInvoke(time);

        lastArgs = args;
        // eslint-disable-next-line
        lastThis = this;
        lastCallTime = time;

        if (isInvoking) {
            if (timerId === undefined) {
                return leadingEdge(lastCallTime);
            }
            if (maxing) {
                // Handle invocations in a tight loop.
                clearTimeout(timerId);
                timerId = setTimeout(timerExpired, wait);
                return invokeFunc(lastCallTime);
            }
        }
        if (timerId === undefined) {
            timerId = setTimeout(timerExpired, wait);
        }
        return result;
    }
    debounced.cancel = cancel;
    debounced.flush = flush;
    return debounced;
}

export const isAnyKeyEmpty = (value: any) => {
    let isEmpty = false;
    Object.values(value).forEach((val) => {
        if (!val) isEmpty = true;
    });
    return isEmpty;
};
